import { useEffect, useState } from 'react';

import type { PolicyDocument, PolicyDocumentType } from '../types/login';

const STORAGE_KEY = 'policyDocumentChecks';

type StoredChecks = { email: string; keys: string[] };

// Ticks are tied to the user and to each document's exact version, so a stale entry never
// pre-ticks a changed document or another user's page in the same tab, while documents that
// didn't change keep their tick after the list is reloaded.
const getKey = (document: PolicyDocument) => `${document.type}:${document.version}`;

const readChecks = (email: string): string[] => {
    try {
        const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') as StoredChecks;
        return stored?.email === email ? stored.keys : [];
    } catch {
        return [];
    }
};

const writeChecks = (value: StoredChecks | null) => {
    try {
        if (value) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // Storage can be blocked; the ticks then only live for this page view.
    }
};

// Kept in sessionStorage because a mobile auth sheet may open a document in place, and Back must
// return to the page with the ticks still set.
export const usePolicyChecks = (email: string | undefined, documents: PolicyDocument[]) => {
    const user = email ?? '';
    const [checkedKeys, setCheckedKeys] = useState<string[]>(() => readChecks(user));

    useEffect(() => {
        setCheckedKeys(readChecks(user));
    }, [user]);

    const checkedTypes: PolicyDocumentType[] = documents
        .filter((d) => checkedKeys.includes(getKey(d)))
        .map((d) => d.type);

    const onCheckedChange = (type: PolicyDocumentType, checked: boolean) => {
        const keys = documents
            .filter((d) => (d.type === type ? checked : checkedTypes.includes(d.type)))
            .map(getKey);
        setCheckedKeys(keys);
        writeChecks({ email: user, keys });
    };

    const reset = () => {
        setCheckedKeys([]);
        writeChecks(null);
    };

    return { checkedTypes, onCheckedChange, reset };
};
